import React, { useState } from "react";
import {
  Button,
  Modal,
  toast,
  useConfirmDialog,
  useWizard,
} from "@picklebet/ui";
import { convertErrorToToast } from "utilities/errorsParser";
import { walletService } from "@/features/wallet/services/wallet";
import { getInteracBankAccountLabel } from "@/features/wallet/utils/interac";
import { OptionRow } from "@/features/wallet/components/deposit/components/deposit-wizard/option-row/OptionRow";
import { OptionRowAdd } from "@/features/wallet/components/deposit/components/deposit-wizard/option-row/OptionAddRow";
import { MethodRowSkeleton } from "@/features/wallet/components/deposit/components/deposit-wizard/method-row/MethodRow";
import { BankMethodIcon } from "@/features/wallet/components/deposit/components/deposit-wizard/method-row/BankMethodIcon";
import { RemoveAction } from "@/features/wallet/components/deposit/components/deposit-wizard/method-row/RemoveAction";
import { WithdrawContent } from "../../blocks/withdraw-content/WithdrawContent";
import type { WithdrawStepName } from "@/features/wallet/components/withdraw/utils/registry";
import { useWithdrawWizard } from "@/features/wallet/components/withdraw/hooks/useWithdrawWizard";
import { useInteracWithdrawal } from "@/features/wallet/components/withdraw/hooks/useInteracWithdrawal";
import { DefaultBadge, SetDefaultBadge } from "./DefaultBadge";

import * as styles from "./InteracAccountStep.module.scss";

export const InteracAccountStep = () => {
  const { back, goTo } = useWizard<WithdrawStepName>();
  const { setInteracWalletId, canManageWallets } = useWithdrawWizard();
  const { interacAddresses, selectedAddress, isLoading } =
    useInteracWithdrawal();
  const { confirm, confirmDialog } = useConfirmDialog();

  // held locally so the choice isn't committed until Done; derived rather
  // than seeded so it still resolves once the addresses finish loading
  const [pendingWalletId, setPendingWalletId] = useState<string>();
  const [settingDefaultId, setSettingDefaultId] = useState<string>();
  const activeWalletId =
    pendingWalletId ?? selectedAddress?.userExternalWalletId;

  const handleDone = () => {
    if (activeWalletId) {
      setInteracWalletId(activeWalletId);
    }

    back();
  };

  const handleRemove = async (walletId: string) => {
    const confirmed = await confirm({
      title: "Remove Interac Details",
      description: "Are you sure you want to remove these Interac details?",
    });

    if (!confirmed) {
      return;
    }

    await walletService.removeInteracBankAccount(walletId);
  };

  // the badge moves once the new default lands in firestore
  const handleSetDefault = async (walletId: string) => {
    setSettingDefaultId(walletId);
    const result = await walletService.setDefaultInteracBankAccount(walletId);
    setSettingDefaultId(undefined);

    if (!result.ok) {
      toast(
        convertErrorToToast(result.error, {
          title: "Unable to set default Interac details",
        }),
      );
    }
  };

  if (isLoading) {
    return (
      <WithdrawContent>
        <MethodRowSkeleton />
        <MethodRowSkeleton />
      </WithdrawContent>
    );
  }

  return (
    <>
      <WithdrawContent>
        <div
          className={styles.options}
          role="radiogroup"
          aria-label="Select Interac Account"
        >
          {interacAddresses.map((address) => (
            <OptionRow
              key={`${address.userExternalWalletId}-${!!address.isDefaultWithdrawalWallet}`}
              label={getInteracBankAccountLabel(address)}
              icon={<BankMethodIcon />}
              sublabel={
                address.isDefaultWithdrawalWallet ? (
                  <DefaultBadge />
                ) : (
                  canManageWallets && (
                    <SetDefaultBadge
                      disabled={!!settingDefaultId}
                      onSetDefault={() =>
                        handleSetDefault(address.userExternalWalletId)
                      }
                    />
                  )
                )
              }
              selected={address.userExternalWalletId === activeWalletId}
              onSelect={() => setPendingWalletId(address.userExternalWalletId)}
              trailing={
                canManageWallets && (
                  <RemoveAction
                    onClick={() => handleRemove(address.userExternalWalletId)}
                  />
                )
              }
            />
          ))}

          <OptionRowAdd
            label="Add Interac Details"
            onClick={() => goTo("withdraw/interac-account/add")}
          />
        </div>

        <Modal.Footer>
          <Button
            className={styles.doneButton}
            onClick={handleDone}
            disabled={!activeWalletId}
          >
            Done
          </Button>
        </Modal.Footer>
      </WithdrawContent>

      {confirmDialog}
    </>
  );
};
